import React, { useState, useContext, useEffect, useMemo } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FaPlus, FaCheck, FaChevronDown, FaChevronUp, FaTrash } from "react-icons/fa";

const WorkTypeMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // ✅ get localStorage stored data
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [estimationTypes, setEstimationTypes] = useState([]);
  const [ddlLoading, setDdlLoading] = useState(false);

  const [formData, setFormData] = useState({
    estimationTypeName: "",
    workTypeCode: "",
    workTypeName: "",
    description: "",
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const [saving, setSaving] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);

  // ✅ Next WorkType Code
  const fetchNextWorkTypeCode = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/ppo_getNextWorkTypeCode`,
        { headers: commonHeaders }
      );

      const nextCode = res.data?.workTypeCode || "WK1";
      setFormData((prev) => ({
        ...prev,
        workTypeCode: nextCode,
      }));
    } catch (err) {
      console.error("Next WorkType Code Error:", err);
      toast.error("Failed to get next work type code");
      setFormData((prev) => ({
        ...prev,
        workTypeCode: prev.workTypeCode || "WK1",
      }));
    }
  };

  // ✅ WorkType List
  // ✅ WorkType List
  const fetchWorkTypeList = async (searchOverride) => {
    try {
      setLoading(true);

      const search =
        searchOverride !== undefined ? searchOverride : searchTerm;

      const res = await axios.get(
        `${BASE_URL}/api/ppo_workTypeList`,
        {
          params: { search },
          headers: commonHeaders,
        }
      );

      const list = res.data?.list || [];

      const mapped = list.map((item, index) => ({
        id: item.fld_Categoryid ?? index + 1,
        sNo: index + 1,
        estimationTypeName: item.fld_EstimationTypename ?? "",
        workTypeCode: item.fld_Categorycode ?? "",
        workTypeName: item.fld_Categoryname ?? "",
        description: item.fld_description ?? "",
        createdOn: item.fld_Createdon ?? null,
        selected: false,
      }));

      setRecords(mapped);
    } catch (err) {
      console.error("Error fetching work type list:", err);
      toast.error("Failed to load work type list");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Estimation DDL + Next Code
  useEffect(() => {
    const fetchEstimationDDL = async () => {
      try {
        setDdlLoading(true);
        const res = await axios.get(
          `${BASE_URL}/api/ppo_estimationTypeDDL`,
          { headers: commonHeaders }
        );

        if (res.data?.success) {
          setEstimationTypes(res.data.list || []);
        } else {
          toast.error(res.data?.message || "Failed to load estimation types");
        }
      } catch (err) {
        console.error("DDL fetch error:", err);
        toast.error("Failed to load estimation types");
      } finally {
        setDdlLoading(false);
      }
    };

    fetchEstimationDDL();
    fetchNextWorkTypeCode();
  }, [commonHeaders]);

  // ✅ WorkType List
  useEffect(() => {
    fetchWorkTypeList();
  }, [commonHeaders]);

  const recordsPerPage = 20;
  const totalPages = Math.ceil(records.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecords = records.slice(startIndex, endIndex);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.estimationTypeName) {
      newErrors.estimationTypeName = "Estimation Type Name is required";
    }

    if (!formData.workTypeCode?.trim()) {
      newErrors.workTypeCode = "Work Type Code is required";
    }

    if (!formData.workTypeName?.trim()) {
      newErrors.workTypeName = "Work Type Name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ Add / Update
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        estimationTypeId: formData.estimationTypeName,
        workTypeCode: formData.workTypeCode,
        workTypeName: formData.workTypeName,
        description: formData.description,
        isActive: formData.isActive,
      };

      // ✅ UPDATE MODE
      if (isEdit && editId) {
        const res = await axios.post(
          `${BASE_URL}/api/ppo_updateWorkType`,
          {
            ...payload,
            categoryId: editId,
          },
          { headers: commonHeaders }
        );

        if (res.data?.success) {
          toast.success(res.data?.message || "Updated successfully");
          await fetchWorkTypeList();

          setIsEdit(false);
          setEditId(null);

          setFormData((prev) => ({
            ...prev,
            estimationTypeName: "",
            workTypeName: "",
            description: "",
            isActive: true,
          }));

          await fetchNextWorkTypeCode();
        } else {
          toast.error(res.data?.message || "Update failed");
        }
        return;
      }

      // ✅ ADD MODE
      const res = await axios.post(
        `${BASE_URL}/api/ppo_addWorkType`,
        payload,
        { headers: commonHeaders }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved successfully");
        await fetchWorkTypeList();

        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          workTypeName: "",
          description: "",
          isActive: true,
        }));

        await fetchNextWorkTypeCode();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const handleAddNew = async () => {
    setIsEdit(false);
    setEditId(null);
    setErrors({});

    setFormData((prev) => ({
      ...prev,
      estimationTypeName: "",
      workTypeName: "",
      description: "",
      isActive: true,
    }));

    await fetchNextWorkTypeCode();
    toast.info("Form cleared for new entry");
  };

  // ✅ Cancel: discard changes, exit edit mode, clear errors
  const handleCancel = async () => {
    const wasEdit = isEdit;

    setIsEdit(false);
    setEditId(null);
    setErrors({});

    setFormData((prev) => ({
      ...prev,
      estimationTypeName: "",
      workTypeName: "",
      description: "",
      isActive: true,
    }));

    if (wasEdit) {
      await fetchNextWorkTypeCode();
    }
  };

  // ✅ Edit fetch by id
  const handleEditById = async (categoryId) => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/ppo_getWorkTypeById`,
        {
          params: { categoryId },
          headers: commonHeaders,
        }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to fetch record");
        return;
      }

      const d = res.data.data;

      setFormData({
        estimationTypeName: d.fld_EstimationTypeid ? String(d.fld_EstimationTypeid) : "",
        workTypeCode: d.fld_Categorycode || "",
        workTypeName: d.fld_Categoryname || "",
        description: d.fld_description || "",
        isActive: d.fld_isactive === 1 || d.fld_isactive === true,
      });

      setIsEdit(true);
      setEditId(categoryId);
      setShowFilters(true);
      setErrors({});
    } catch (err) {
      console.error("Edit fetch error:", err);
      toast.error(err.response?.data?.message || "Server error");
    }
  };

  // ✅ Delete Single
  // ✅ Delete Single
  const handleDeleteSingle = async () => {
    // Prefer the record being edited; otherwise fall back to a
    // single checkbox-selected record from the table.
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    let targetId = null;

    if (isEdit && editId) {
      targetId = editId;
    } else if (selectedIds.length === 1) {
      targetId = selectedIds[0];
    } else if (selectedIds.length > 1) {
      toast.info("Please select only one record to delete, or use Delete Selected");
      return;
    }

    if (!targetId) {
      toast.info("Please click Work Type Code to select a record");
      return;
    }

    const ok = window.confirm("Are you sure you want to delete this Work Type?");
    if (!ok) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/ppo_deleteWorkType`,
        {
          categoryId: targetId,
        },
        { headers: commonHeaders }
      );

            if (res.data?.success) {
        toast.success(res.data?.message || "Deleted successfully");
        await fetchWorkTypeList();

        // Uncheck the row we just deleted, if it was selected via checkbox
        setRecords((prev) =>
          prev.map((r) =>
            r.id === targetId ? { ...r, selected: false } : r
          )
        );

        setIsEdit(false);
        setEditId(null);
        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          workTypeName: "",
          description: "",
          isActive: true,
        }));
        setErrors({});
        await fetchNextWorkTypeCode();
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.response?.data?.message || "Server error while deleting");
    } finally {
      setSaving(false);
    }
  };

  // ✅ Delete Selected
  const handleDeleteSelected = async () => {
    const selectedIds = records.filter((r) => r.selected).map((r) => r.id);

    if (selectedIds.length === 0) {
      toast.info("Please select at least one row to delete");
      return;
    }

    const ok = window.confirm(`Delete ${selectedIds.length} selected record(s)?`);
    if (!ok) return;

    try {
      setSaving(true);

      for (const id of selectedIds) {
        await axios.post(
          `${BASE_URL}/api/ppo_deleteWorkType`,
          {
            categoryId: id,
          },
          { headers: commonHeaders }
        );
      }

      toast.success("Selected records deleted successfully");
      await fetchWorkTypeList();

      if (isEdit && editId && selectedIds.includes(editId)) {
        setIsEdit(false);
        setEditId(null);
        setFormData((prev) => ({
          ...prev,
          estimationTypeName: "",
          workTypeName: "",
          description: "",
          isActive: true,
        }));
        setErrors({});
        await fetchNextWorkTypeCode();
      }
    } catch (err) {
      console.error("Bulk delete error:", err);
      toast.error("Error deleting selected records");
    } finally {
      setSaving(false);
    }
  };

    // ✅ Search
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(1);
    fetchWorkTypeList(searchTerm);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchWorkTypeList("");
  };

  // ✅ Checkbox select
  const handleSelectRecord = (id, checked) => {
    setRecords((prev) =>
      prev.map((row) => (row.id === id ? { ...row, selected: checked } : row))
    );
  };

   const handlePageChange = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const half = Math.floor(maxVisiblePages / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + maxVisiblePages - 1);

    if (end - start + 1 < maxVisiblePages) {
      start = Math.max(1, end - maxVisiblePages + 1);
    }

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    for (let i = start; i <= end; i++) pages.push(i);

    if (end < totalPages) {
      if (end < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">WORK TYPE MASTER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white text-black`
          }`}
        >
          {/* Header Section with Hide/Show Filter Button */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Work Type Master
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {records.length} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Search estimation, code, name..."
                  className={`px-3 py-2 rounded-lg border text-sm w-64 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
                <button
                  onClick={handleSearchSubmit}
                  className={`px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white text-sm`}
                >
                  Search
                </button>
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className={`px-4 py-2 rounded-lg font-semibold border transition-all duration-200 text-sm ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white border-gray-600"
                        : "bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300"
                    }`}
                  >
                    Clear
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Entry Form" : "Show Entry Form"}
              </button>
            </div>
          </div>

          {/* Form Section */}
          <div
            className={`rounded-xl border mb-6 overflow-hidden transition-all duration-300 ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {showFilters && (
              <div
                className={`transition-all duration-300 ease-in-out ${
                  showFilters ? "max-h-none opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                }`}
              >
                <div
                  className={`p-6 border-t ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <div className="max-w-2xl mx-auto space-y-4">
                    {/* Estimation Type Name */}
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Estimation Type Name <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-8">
                        <select
                          value={formData.estimationTypeName}
                          disabled={isEdit}
                          onChange={(e) =>
                            handleInputChange("estimationTypeName", e.target.value)
                          }
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors.estimationTypeName ? "border-red-500" : ""
                          } ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        >
                          <option value="">
                            {ddlLoading ? "Loading..." : "Select"}
                          </option>

                          {estimationTypes.map((t) => (
                            <option key={t.estimationTypeId} value={t.estimationTypeId}>
                              {t.estimationTypeName}
                            </option>
                          ))}
                        </select>
                        {errors.estimationTypeName && (
                          <p className="text-red-500 text-sm mt-1">
                            {errors.estimationTypeName}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Work Type Code */}
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Work Type Code <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-8">
                        <input
                          type="text"
                          value={formData.workTypeCode}
                          disabled
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors.workTypeCode ? "border-red-500" : ""
                          } ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter work type code..."
                        />
                        {errors.workTypeCode && (
                          <p className="text-red-500 text-sm mt-1">{errors.workTypeCode}</p>
                        )}
                      </div>
                    </div>

                    {/* Work Type Name */}
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Work Type Name <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <div className="col-span-8">
                        <input
                          type="text"
                          value={formData.workTypeName}
                          onChange={(e) => handleInputChange("workTypeName", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors.workTypeName ? "border-red-500" : ""
                          } ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter work type name..."
                        />
                        {errors.workTypeName && (
                          <p className="text-red-500 text-sm mt-1">{errors.workTypeName}</p>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4">
                        <label
                          className={`text-sm font-medium ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Description
                        </label>
                      </div>
                      <div className="col-span-8">
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) => handleInputChange("description", e.target.value)}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                              : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                          }`}
                          placeholder="Enter description..."
                        />
                      </div>
                    </div>

                    {/* Active Checkbox */}
                    <div className="grid grid-cols-12 gap-4 items-center">
                      <div className="col-span-4"></div>
                      <div className="col-span-8">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="isActive"
                            checked={formData.isActive}
                            onChange={(e) => handleInputChange("isActive", e.target.checked)}
                            className={`mr-2 rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                          <label
                            htmlFor="isActive"
                            className={`text-sm font-medium ${
                              isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                          >
                            Is Active
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {/* Action Buttons */}
                    <div className="flex justify-center gap-4 pt-4">
                      <button
                        onClick={handleSubmit}
                        disabled={saving}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                      >
                        <FaCheck />
                        {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                      </button>

                      <button
                        onClick={handleCancel}
                        disabled={saving}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60 ${
                          isDark
                            ? "bg-gray-600 hover:bg-gray-500 text-white"
                            : "bg-gray-300 hover:bg-gray-400 text-gray-800"
                        }`}
                      >
                        Cancel
                      </button>

                      <button
                        onClick={handleAddNew}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                      >
                        <FaPlus />
                        Add New
                      </button>

                      <button
                        onClick={handleDeleteSingle}
                        disabled={saving}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isDark
                            ? "bg-red-600 hover:bg-red-700 text-white"
                            : "bg-red-600 hover:bg-red-700 text-white"
                        } disabled:opacity-60`}
                      >
                        <FaTrash />
                        Delete
                      </button>

                      <button
                        onClick={handleDeleteSelected}
                        disabled={saving}
                        className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isDark
                            ? "bg-red-500/80 hover:bg-red-600 text-white"
                            : "bg-red-500 hover:bg-red-600 text-white"
                        } disabled:opacity-60`}
                      >
                        <FaTrash />
                        Delete Selected
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Records Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      S.No
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Estimation Type Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Work Type Code
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Work Type Name
                    </th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                      Description
                    </th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td
                        colSpan="6"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        Loading work types...
                      </td>
                    </tr>
                  ) : currentRecords.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className={`px-4 py-6 text-center text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-600"
                        }`}
                      >
                        No records found
                      </td>
                    </tr>
                  ) : (
                    currentRecords.map((record, index) => (
                      <tr
                        key={record.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.sNo}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.estimationTypeName}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 font-medium ${
                            isDark ? "text-blue-300" : "text-blue-600"
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => handleEditById(record.id)}
                            className="underline hover:opacity-80"
                            title="Click to edit"
                          >
                            {record.workTypeCode}
                          </button>
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.workTypeName}
                        </td>

                        <td
                          className={`px-4 py-3 text-center text-sm border-r border-gray-200 dark:border-gray-700 ${
                            isDark ? "text-gray-300" : "text-gray-900"
                          }`}
                        >
                          {record.description || "-"}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!record.selected}
                            onChange={(e) =>
                              handleSelectRecord(record.id, e.target.checked)
                            }
                            className={`h-4 w-4 cursor-pointer rounded focus:ring-2 focus:ring-blue-500 ${
                              isDark
                                ? "bg-gray-700 border-gray-600 text-blue-600"
                                : "bg-white border-gray-300 text-blue-600"
                            }`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {/* Pagination */}
          <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === 1
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              {renderPageNumbers().map((page, index) => (
                <button
                  key={index}
                  onClick={() => page !== "..." && handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                  currentPage === totalPages || totalPages === 0
                    ? isDark
                      ? "bg-gray-800 text-gray-500 border-gray-700 cursor-not-allowed"
                      : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default WorkTypeMaster;